// ---------------------------------------------------------------------------
// src/pages/KPI/reportConfig.js
//
// SINGLE SOURCE OF TRUTH for the KPI / Reports hub.
//
// Every report shown on /kpi is ONE entry in REPORTS below. To add a report
// later, append an object here — no component changes required.
//
// ── Field reference ────────────────────────────────────────────────────────
//   id          unique slug (stable; used as React key + "recently opened")
//   title       card title (display name)
//   category    one of CATEGORY keys below (drives the tab it appears under)
//   source      "powerbi" | "ssrs" | "external" | "internal"
//                 powerbi  — Power BI report
//                 ssrs     — legacy SSRS label (X-Ray is "internal" now; see note)
//                 external — non-PowerBI web link (Fabric, Looker, Google, etc.)
//                 internal — rendered by THIS app from a backend endpoint
//                            (X-Ray, wired in Round 3)
//   embed       "iframe" | "newtab" | "internal"
//                 iframe   — safe <iframe> inside the hub (publish-to-web links)
//                 newtab   — opens in a new browser tab (auth-gated links)
//                 internal — opens an in-app route/panel (X-Ray, Round 3)
//   url         the link. For embed:"newtab" this is the href. For
//               embed:"internal" this is the in-app route. For embed:"iframe"
//               it is null here: the publish-to-web link is served by the API
//               (GET /api/reports/embed-urls, same per-report gate) and the
//               hub fetches it before rendering the iframe — see the note
//               above REPORTS. NEVER invent these — they come from the
//               uploaded Power BI Reports document, verbatim.
//   status      "ready" | "planned" | "notFinalized" | "needsConfig"
//   icon        ri-* icon class
//   preview     OPTIONAL preview image URL (a dashboard screenshot). Served
//               from the public folder, e.g. "/assets/report-previews/xray.png".
//               Drop a PNG/JPG there to give a card a thumbnail; if omitted (or
//               the image fails to load) the card shows a clean gradient + icon
//               placeholder, so nothing ever looks blank. No rebuild needed to
//               add an image — it's a static asset referenced by URL.
//   description one-line helper under the title
//   permission  (auth) => boolean  — per-report visibility gate. Receives the
//               useAuth() object. Return true to show the card. See GATES.
//   note        optional reviewer note (not shown to users)
//
// ── Permission model (PURE PERMISSION) ─────────────────────────────────────
//   Each report carries its OWN gate via `permission`. Access is driven ONLY by
//   KPI permissions; the only bypass is IT Admin. Role/identity flags
//   (President, Finance, Manager, Super User) and "has reports" do NOT grant KPI
//   reports — if the matching KPI permission isn't granted, the card isn't
//   visible. (Some report lines still pass a legacy `role:` option to
//   GATES.category; it is intentionally IGNORED by the factory.)
//
//   A report shows if the user has its category permission (e.g.
//   "KPI - Finance Reports"), or its specific permission (e.g. "KPI - X-Ray
//   Report"), or is IT Admin. To grant access: tick the relevant KPI permission
//   on a role (Role Permissions) or a person (Direct User Permissions).
// ---------------------------------------------------------------------------

// Category definitions — order here = tab order in the hub.
export const CATEGORIES = [
    { key: "all",       label: "All Reports", icon: "ri-apps-2-line" },
    { key: "finance",   label: "Finance",     icon: "ri-money-dollar-circle-line" },
    { key: "sales",     label: "Sales",       icon: "ri-line-chart-line" },
    { key: "inventory", label: "Inventory",   icon: "ri-archive-line" },
    { key: "orders",    label: "Orders",      icon: "ri-shopping-bag-line" },
    { key: "quality",   label: "Quality",     icon: "ri-shield-check-line" },
    { key: "vendor",    label: "Vendor",      icon: "ri-truck-line" },
    { key: "planning",  label: "Planning",    icon: "ri-compass-3-line" },
    { key: "planned",   label: "Planned",     icon: "ri-time-line" },
];

// ── Permission gates ────────────────────────────────────────────────────────
// Each gate is (auth) => boolean, where `auth` is the useAuth() object.
//
// Round 6: gates are now PERMISSION-AWARE so IT Admin can grant access
// per-user from Admin > User Permissions, for current AND future reports —
// WITHOUT a code change. A report is visible if ANY of these is true:
//   (a) the user was granted the report's specific permission,
//   (b) the user was granted the report's CATEGORY permission,
//   (c) the user qualifies by ROLE (legacy behavior — nothing breaks), or
//   (d) the user is IT Admin (always passes).
//
// To add a FUTURE report: give it a `permission` built from GATES.category(...)
// (and optionally a specific perm). Granting "KPI - <Category> Reports" to a
// user then covers it automatically — no redeploy.

// hasAccess helper that tolerates a missing auth/hasAccess.
const can = (auth, permName) => {
    try { return !!(auth && typeof auth.hasAccess === "function" && auth.hasAccess(permName)); }
    catch { return false; }
};

export const GATES = {
    // PURE PERMISSION model (only IT Admin bypasses):
    // A report card shows ONLY if the user has the matching KPI permission.
    // Role/identity flags (President, Finance, Manager, Super User) and
    // "has reports" do NOT grant KPI reports — if the KPI permission isn't
    // granted, the card isn't visible. This matches the backend KPI policies.

    // "Any KPI-hub user" reports: require the hub permission (or any category/
    // specific perm the report itself also checks) or IT Admin. NOT true-for-all.
    kpi: (a) => can(a, "KPI - View Hub") || !!a?.isItAdmin,

    // Finance / management reports — require the finance KPI permission.
    finance: (a) =>
        can(a, "KPI - Finance Reports") ||
        !!a?.isItAdmin,

    // Vendor / manager-oriented reports — require the relevant KPI permission.
    manager: (a) =>
        can(a, "KPI - Vendor Reports") ||
        !!a?.isItAdmin,

    // Factory for a category gate. Honors the category permission and an
    // optional specific per-report permission, plus IT Admin. The legacy
    // `opts.role` fallback is intentionally IGNORED now — KPI access is purely
    // permission-driven, so a role can no longer unlock a report. (Kept in the
    // signature so the many existing call sites still compile unchanged.)
    category: (categoryPerm, opts = {}) => (a) =>
        can(a, categoryPerm) ||
        (opts.specific && can(a, opts.specific)) ||
        !!a?.isItAdmin,
};

// ── Power BI publish-to-web links (embeddable via iframe) ───────────────────
// Go-live parity (2026-09-24): publish-to-web links need no sign-in, so they
// no longer ship in this (public) bundle. Every embed:"iframe" report keeps
// url: null here; its link lives in sswDashboardAPI ReportsController
// (EmbedReports, keyed by the same `id`) and is returned by
// GET /api/reports/embed-urls only to users who pass that report's
// `permission` gate below. Keep the ids in sync when adding a report.

export const REPORTS = [
    // ── Finance / AR / AP ───────────────────────────────────────────────────
    {
        id: "aged-receivables-sswhite",
        title: "Aged Receivables — SS White",
        category: "finance",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-money-dollar-circle-line",
        description: "AR aging — SS White Technologies",
        permission: GATES.category("KPI - Finance Reports", { specific: "KPI - Aged Receivables", role: (a) => a?.isFinance || a?.isPresident }),
    },
    {
        id: "aged-receivables-shukla",
        title: "Aged Receivables — Shukla Medical",
        category: "finance",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-money-dollar-circle-line",
        description: "AR aging — Shukla Medical",
        permission: GATES.category("KPI - Finance Reports", { specific: "KPI - Aged Receivables", role: (a) => a?.isFinance || a?.isPresident }),
        note: "Doc gave a plain publish-to-web link (no iframe wrapper). Same format, embeds fine.",
    },
    {
        id: "aged-payables",
        title: "Aged Payables",
        category: "finance",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-bank-card-line",
        description: "AP aging across companies",
        permission: GATES.category("KPI - Finance Reports", { specific: "KPI - Aged Payables", role: (a) => a?.isFinance || a?.isPresident }),
    },
    {
        id: "variance-report",
        title: "Variance Report",
        category: "finance",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-scales-3-line",
        description: "Budget vs actual variance",
        permission: GATES.category("KPI - Finance Reports", { specific: "KPI - Variance Report", role: (a) => a?.isFinance || a?.isPresident }),
        note: "Plain publish-to-web link in doc; embeddable.",
    },

    // ── X-Ray (internal — rebuilt from RDL SQL in Round 3) ──────────────────
    {
        id: "xray-report",
        title: "X-Ray Report",
        category: "finance",
        source: "internal",
        embed: "internal",
        url: "/kpi/xray", // in-app route (Round 3)
        status: "ready",
        icon: "ri-radar-line",
        preview: "/assets/report-previews/xray-report.png",
        description: "Management finance health — billing, shipments, orders, potential billing",
        permission: GATES.category("KPI - Finance Reports", { specific: "KPI - X-Ray Report", role: (a) => a?.isFinance || a?.isPresident }),
        featured: true,
        note: "Round 3: backend reads RDL SQL via KineticConnection (read-only), rendered as React tables with a SelectDate filter (default today).",
    },

    // ── Orders / Billing / Shipping ─────────────────────────────────────────
    {
        id: "backlog-orders",
        title: "Backlog Orders",
        category: "orders",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-stack-line",
        description: "Orders in backlog",
        permission: GATES.category("KPI - Orders Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
    },
    {
        id: "open-orders",
        title: "Open Orders",
        category: "orders",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-shopping-bag-line",
        description: "Currently open orders",
        permission: GATES.category("KPI - Orders Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
    },

    // ── Inventory ────────────────────────────────────────────────────────────
    {
        id: "inventory-performance",
        title: "Inventory Performance Report",
        category: "inventory",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-archive-line",
        description: "Inventory performance & turns",
        permission: GATES.category("KPI - Inventory Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
        note: "Doc also lists a Power BI share link; the publish-to-web embed (served by the API) is used for in-hub rendering.",
    },
    {
        id: "inventory-report",
        title: "Inventory Report",
        category: "planned",
        source: "powerbi",
        embed: "newtab",
        url: null,
        status: "planned",
        icon: "ri-archive-2-line",
        description: "Ongoing — not yet published",
        permission: GATES.category("KPI - Inventory Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
        note: "Doc lists 'Inventory Report - Ongoing' with no link.",
    },

    // ── Sales ────────────────────────────────────────────────────────────────
    {
        id: "shukla-sales-dashboard",
        title: "Shukla Medical Sales Dashboard",
        category: "sales",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-line-chart-line",
        description: "Shukla Medical sales",
        permission: GATES.category("KPI - Sales Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
    },
    {
        id: "sales-by-prodgroup",
        title: "Sales By Product Group Dashboard",
        category: "sales",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-bar-chart-grouped-line",
        description: "Sales grouped by product group",
        permission: GATES.category("KPI - Sales Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
    },
    {
        id: "customer-profile-sales",
        title: "Customer Profile — Sales",
        category: "sales",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-user-search-line",
        description: "Sales by customer profile",
        permission: GATES.category("KPI - Sales Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
    },
    {
        id: "shukla-sales-rental",
        title: "Shukla Sales & Rental Reports",
        category: "sales",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-exchange-dollar-line",
        description: "MTD sales, by product / rep / region / customer",
        permission: GATES.category("KPI - Sales Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
        note: "Doc lists 5 sub-views (MTD, by product, rep, region, customer) all pointing at this same report; rendered as one card.",
    },
    {
        id: "sales-performance",
        title: "Sales Performance",
        category: "planned",
        source: "powerbi",
        embed: "newtab",
        url: null,
        status: "planned",
        icon: "ri-line-chart-line",
        description: "Ongoing — not yet published",
        permission: GATES.category("KPI - Sales Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
        note: "Doc lists 'Sales Performance - Ongoing' with no link.",
    },

    // ── Quality ──────────────────────────────────────────────────────────────
    // Owner request (2026-07): DMR Performance Quality — publish-to-web link
    // on the newer Microsoft Fabric domain (same format as Power BI; embeds
    // identically). Go-live parity (2026-09-24): the link is served by the
    // API like every other embed:"iframe" report.
    {
        id: "dmr-performance-quality",
        title: "DMR Performance Quality",
        category: "quality",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "ready",
        icon: "ri-shield-star-line",
        description: "DMR performance — quality metrics",
        permission: GATES.category("KPI - Quality Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
    },
    {
        id: "rma-kpi-quality",
        title: "RMA KPI — Quality Team",
        category: "quality",
        source: "powerbi",
        embed: "iframe",
        url: null,
        status: "notFinalized",
        icon: "ri-shield-check-line",
        description: "RMA KPIs (not finalized)",
        permission: GATES.category("KPI - Quality Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
        note: "Doc marks this 'Not Finalized'. Link present and embeddable; shown with a Not Finalized badge.",
    },

    // ── Vendor / Purchasing ──────────────────────────────────────────────────
    {
        id: "vendor-performance-kpi",
        title: "Vendor Performance KPI Report",
        category: "vendor",
        source: "external",
        embed: "newtab",
        url: "https://app.powerbi.com/groups/me/reports/141cffb0-8e22-400d-926e-6c83cdc0c70e/799f5b95deff9579faaf?ctid=ab1b496f-cfe3-4ea3-83b5-c8b37925f8c0&experience=power-bi",
        status: "needsConfig",
        icon: "ri-truck-line",
        description: "Vendor scorecard, trends, summary (sign-in required)",
        permission: GATES.category("KPI - Vendor Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
        note: "Power BI workspace (/groups/me/) link — auth required, will not iframe-embed. Open in new tab.",
    },

    // ── Planned / Ongoing / Other ────────────────────────────────────────────
    {
        id: "mfg-gl-cos-variance",
        title: "SSW MFG GL Data COS — Variance Accts",
        category: "planned",
        source: "powerbi",
        embed: "newtab",
        url: null,
        status: "planned",
        icon: "ri-file-list-3-line",
        description: "Ongoing — not yet published",
        permission: GATES.category("KPI - Finance Reports", { role: (a) => a?.isFinance || a?.isPresident }),
        note: "Doc lists 'SSW MFG GL Data COS – Variance Accts - Ongoing' with no link. Finance-gated (GL data).",
    },

    // ── Planning (internal — rebuilt from RDL SQL, Round 11) ──────────────────
    {
        id: "summary-otd",
        title: "Summary On-Time Delivery",
        category: "planning",
        source: "internal",
        embed: "internal",
        url: "/kpi/otd/summary-otd",
        status: "ready",
        icon: "ri-pie-chart-2-line",
        description: "OTD scorecard — on-time % by business segment",
        permission: GATES.category("KPI - Planning Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
        note: "Round 11: backend reads SummaryOTD.sql via KineticConnection (read-only), date-range params.",
    },
    // Owner request (2026-07): Sales Dashboards — Planning. Google Apps
    // Script web app; Apps Script sends X-Frame-Options, so it cannot be
    // iframed — opens in a new tab (embed:"newtab"), same pattern the hub
    // already supports for sign-in-required reports. The live-preview tier
    // is skipped automatically for non-iframe reports.
    {
        id: "sales-dashboards-planning",
        title: "Sales Dashboards — Planning",
        category: "planning",
        source: "google",
        embed: "newtab",
        url: "https://script.google.com/a/macros/sswhite.net/s/AKfycbza-WqpXFwGgt1zwqLqKIYrM9JbbnTZq1mestNaaxhWFkZWzkb4IR_TP_zpsAiullGQ/exec",
        status: "ready",
        icon: "ri-line-chart-line",
        description: "Sales dashboards for planning (opens in new tab)",
        permission: GATES.category("KPI - Planning Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
    },
    {
        id: "blown-up-otd",
        title: "Blown-Up OTD Report",
        category: "planning",
        source: "internal",
        embed: "internal",
        url: "/kpi/otd/blown-up",
        status: "ready",
        icon: "ri-file-search-line",
        description: "Order-level OTD detail with chart and per-order drilldown",
        permission: GATES.category("KPI - Planning Reports", { role: (a) => a?.isManager || a?.isSuperUser }),
        note: "Round 11: backend reads BlownUpReport.sql via KineticConnection (read-only), date-range + optional filters.",
    },
];

// ── Status display metadata (badge + open-behavior) ─────────────────────────
export const STATUS_META = {
    ready:        { label: "Ready",         badge: "ok",       openable: true  },
    notFinalized: { label: "Not Finalized", badge: "warn",     openable: true  },
    needsConfig:  { label: "Opens in tab",  badge: "neutral",  openable: true  },
    planned:      { label: "Planned",       badge: "neutral",  openable: false },
};

// Convenience: return reports the current user may see, given a useAuth() obj.
export function visibleReports(auth) {
    return REPORTS.filter((r) => {
        try { return typeof r.permission === "function" ? r.permission(auth) : true; }
        catch { return false; }
    });
}

// Preview-image URL for a report. Uses an explicit `preview` if set, otherwise
// the convention /assets/report-previews/<id>.png. Drop a screenshot there to
// give any card a thumbnail — no config change needed. If the file is absent
// the card falls back to a gradient placeholder (handled in the card).
export function previewUrl(report) {
    if (!report) return null;
    if (report.preview) return report.preview;
    const base = (typeof process !== "undefined" && process.env && process.env.PUBLIC_URL) || "";
    return `${base}/assets/report-previews/${report.id}.png`;
}

export default REPORTS;
